"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

const MIN_ADVANTAGES = 2
const MAX_ADVANTAGES = 4

// --- Icons ---

function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <rect x="3" y="1" width="4" height="14" rx="1" />
      <rect x="9" y="1" width="4" height="14" rx="1" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#000" aria-hidden focusable="false">
      <path d="M3 1.5v13l11-6.5L3 1.5z" />
    </svg>
  )
}

function ArrowIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
      <path
        d={dir === "left" ? "M20 12H4M10 6l-6 6 6 6" : "M4 12h16M14 6l6 6-6 6"}
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// --- Viewport entrance hook (fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 64px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 112px 120px;
  }
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 80px;
  }
`

/* staggered entrance: header, then advantages, then media */
const Reveal = styled.div<{ $in: boolean; $delay: number }>`
  width: 100%;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "24px")});
  transition: opacity 0.6s ease, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => $delay}ms;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 840px;
  margin: 0 auto;
`

/* Rich-text title: the emphasized word carries the Figma brush underline. */
const Title = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    position: relative;
    display: inline-block;
    font-weight: inherit;
    text-decoration: none;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    font-weight: 500;

    strong {
      font-weight: 700;
    }
  }
`

const IntroText = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 8px 0 0;
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
    margin-top: 24px;
  }
`

/* filled CTA — theme vars keep it high-contrast in every colour package */
const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  background-color: var(--comp-heading);
  color: var(--comp-bg);
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-heading);
    outline-offset: 2px;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: auto;
    min-width: 300px;
    height: 48px;
    font-size: 20px;
    font-weight: 700;
  }
`

/* content block: advantages + media, order swapped by the CMS toggle */
const Content = styled.div<{ $swap: boolean }>`
  display: flex;
  flex-direction: ${({ $swap }) => ($swap ? "column-reverse" : "column")};
  align-items: center;
  gap: 48px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 64px;
  }
`

const Advantages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: flex-start;
  }
`

const Advantage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 24px;
  text-align: center;
  border-bottom: 1px solid color-mix(in srgb, var(--comp-text) 100%, transparent);

  &:last-child {
    border-bottom: none;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    flex: 1 1 0;
    min-width: 0;
    border-bottom: none;
  }
`

const AdvantageTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
  }
`

const AdvantageText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 0;
  white-space: pre-line;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    font-weight: 500;
  }
`

/* media: 335x210 ratio on mobile, 1200x718 on desktop */
const MediaWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 335 / 210;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    aspect-ratio: 1200 / 718;
  }
`

const Slide = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.6s ease;
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
`

const MediaImage = styled(Image)`
  object-fit: cover;
`

const MediaVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Controls = styled.div`
  position: absolute;
  bottom: 16px;
  inset-inline-start: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 2;
`

const RoundButton = styled.button<{ $size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size || 32}px;
  height: ${({ $size }) => $size || 32}px;
  border-radius: 50%;
  border: 1px solid #000;
  background: #fff;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #fff;
    outline-offset: 2px;
  }
`

const PlayPauseBtn = styled(RoundButton)`
  border: none;
`

const GalleryPlayPauseBtn = styled(RoundButton)`
  position: absolute;
  bottom: 16px;
  inset-inline-end: 16px;
  z-index: 3;
  border: none;
`

// --- Types & parsing ---

type MediaItem = { url: string; alt: string }
type AdvantageItem = { title: string; description: string }

function isVideoUrl(url: string): boolean {
  return url.endsWith(".mp4") || url.endsWith(".webm")
}

/* Umbraco returns the toggle as a string (or a single-item array of one). */
function isToggleOn(raw: any): boolean {
  const v = Array.isArray(raw) ? raw[0] : raw
  return String(v).toLowerCase() === "true" || String(v) === "1"
}

function parseAdvantage(block: any): AdvantageItem {
  return {
    title: (extractProperty(block, "title") as string) || "",
    description: (extractProperty(block, "description") as string) || "",
  }
}

/* Video slide inside a mixed gallery — plays programmatically when active,
   with a play/pause overlay button (same pattern as Gallery6). */
function GalleryVideoSlide4({
  url,
  isActive,
  isRtl,
}: {
  url: string
  isActive: boolean
  isRtl: boolean
}) {
  const analytics = useLandingAnalytics()
  const ref = useRef<HTMLVideoElement>(null)
  const [paused, setPaused] = useState(true)

  useEffect(() => {
    if (ref.current && isActive) {
      ref.current.currentTime = 0
      ref.current.play()
      setPaused(false)
    } else if (ref.current) {
      ref.current.pause()
      setPaused(true)
    }
  }, [isActive])

  return (
    <>
      <MediaVideo
        ref={ref}
        src={getUmbracoMediaUrl(url)}
        muted
        loop
        playsInline
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
      />
      <GalleryPlayPauseBtn
        $size={40}
        onClick={() => {
          if (ref.current) {
            analytics.mediaClick(paused ? "play" : "pause", LANDING_ELEMENTS.aboutOpening)
            if (paused) ref.current.play()
            else ref.current.pause()
          }
        }}
        aria-label={
          paused ? (isRtl ? "הפעל וידאו" : "Play video") : isRtl ? "עצור וידאו" : "Pause video"
        }
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </GalleryPlayPauseBtn>
    </>
  )
}

// --- Main Component ---

function AboutOpeningComponent4({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const { ref, inView } = useInViewOnce<HTMLDivElement>()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [slide, setSlide] = useState(0)

  const title = (extractProperty(data, "title") as string) || ""
  const introText = (extractProperty(data, "introText") as string) || ""
  const ctaLinks = extractProperty(data, "ctaButton") as any[] | ""
  const cta = (Array.isArray(ctaLinks) && ctaLinks[0]) || null
  const swap = isToggleOn(extractProperty(data, "swapPosition"))
  const advantageBlocks = extractProperty(data, "advantages") as any[] | ""
  const advantages = (Array.isArray(advantageBlocks) ? advantageBlocks : [])
    .map(parseAdvantage)
    .filter((a) => a.title || a.description)
    .slice(0, MAX_ADVANTAGES)
  const rawMedia = (extractProperty(data, "media", true) as any[] | undefined) || []
  const mediaItems: MediaItem[] = rawMedia
    .filter((m) => m?.url)
    .map((m) => ({ url: m.url as string, alt: (m.altText as string) || "" }))

  // When both video and images are in the media field, show only the video
  // (the design intends a single media type per block; mixed = video takes priority)
  const videoItem = mediaItems.find((m) => isVideoUrl(m.url))
  const isVideo = mediaItems.length === 1 && videoItem !== undefined
  const isGallery = !isVideo && mediaItems.length > 1

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (isPlaying) v.pause()
    else v.play()
    analytics.mediaClick(isPlaying ? "pause" : "play", LANDING_ELEMENTS.aboutOpening)
    setIsPlaying(!isPlaying)
  }

  const go = (delta: number) => setSlide((s) => (s + delta + mediaItems.length) % mediaItems.length)

  if (!title && !introText && advantages.length === 0 && mediaItems.length === 0) return null

  const ctaHref = cta?.url ? getLinkHrefFromMultiUrlPickerItem(cta) : undefined

  return (
    <Section ref={ref}>
      <Inner>
        <Reveal $in={inView} $delay={0}>
          <Header>
            {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
            {introText && <IntroText>{introText}</IntroText>}
            {/* no button element at all when the CMS field is empty */}
            {ctaHref && cta?.name && (
              <Cta
                href={ctaHref}
                onClick={(e) => {
                  analytics.ctaClick(cta.name, LANDING_ELEMENTS.aboutOpening)
                  handleAnchorScroll(e, ctaHref)
                }}
                {...(cta.target === "_blank"
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {cta.name}
              </Cta>
            )}
          </Header>
        </Reveal>

        {(advantages.length >= MIN_ADVANTAGES || mediaItems.length > 0) && (
          <Content $swap={swap}>
            {advantages.length >= MIN_ADVANTAGES && (
              <Reveal $in={inView} $delay={150}>
                <Advantages>
                  {advantages.map((a, i) => (
                    <Advantage key={i}>
                      {a.title && <AdvantageTitle>{a.title}</AdvantageTitle>}
                      {a.description && <AdvantageText>{a.description}</AdvantageText>}
                    </Advantage>
                  ))}
                </Advantages>
              </Reveal>
            )}

            {mediaItems.length > 0 && (
              <Reveal $in={inView} $delay={300}>
                <MediaWrapper>
                  {isVideo && videoItem ? (
                    <>
                      <MediaVideo
                        ref={videoRef}
                        src={getUmbracoMediaUrl(videoItem.url)}
                        muted
                        loop
                        playsInline
                      />
                      <Controls>
                        <PlayPauseBtn
                          $size={40}
                          onClick={togglePlay}
                          aria-label={
                            isPlaying
                              ? isRtl
                                ? "עצור תנועת הרכיב"
                                : "Stop animation"
                              : isRtl
                              ? "הפעל תנועה רכיב"
                              : "Play animation"
                          }
                        >
                          {isPlaying ? <PauseIcon /> : <PlayIcon />}
                        </PlayPauseBtn>
                      </Controls>
                    </>
                  ) : (
                    <>
                      {mediaItems.map((item, i) => (
                        <Slide key={i} $active={i === slide}>
                          {isVideoUrl(item.url) ? (
                            <GalleryVideoSlide4
                              url={item.url}
                              isActive={i === slide}
                              isRtl={isRtl}
                            />
                          ) : (
                            <MediaImage
                              src={getUmbracoMediaUrl(item.url)}
                              fill
                              alt={item.alt}
                              sizes="(max-width: 768px) 100vw, 1200px"
                            />
                          )}
                        </Slide>
                      ))}
                      {isGallery && (
                        <Controls>
                          <RoundButton
                            onClick={() => go(-1)}
                            aria-label={isRtl ? "הקודם" : "Previous"}
                          >
                            <ArrowIcon dir={isRtl ? "right" : "left"} />
                          </RoundButton>
                          <RoundButton onClick={() => go(1)} aria-label={isRtl ? "הבא" : "Next"}>
                            <ArrowIcon dir={isRtl ? "left" : "right"} />
                          </RoundButton>
                        </Controls>
                      )}
                    </>
                  )}
                </MediaWrapper>
              </Reveal>
            )}
          </Content>
        )}
      </Inner>
    </Section>
  )
}

export default AboutOpeningComponent4
